import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { from, lastValueFrom, mergeMap } from 'rxjs'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { OCRScopeWork } from '@/features/jobs/scoping/OCRScopeWork'

/**
 * Dialog that runs a bulk action and shows its progress.
 */
export class OCRBulkProgressDialogViewModel {
  readonly modal = new ModalViewModel()

  @observable accessor selectedCount = 0
  @observable accessor completedCount = 0
  @observable accessor errorCount = 0
  @observable accessor cancelled = false

  @computed
  get completedSuccessfullyCount() {
    return this.completedCount - this.errorCount
  }

  @computed
  get hasErrors() {
    return this.errorCount > 0
  }

  @computed
  get progress() {
    if (this.selectedCount === 0) {
      return 0
    }

    if (!this.process.pending) {
      return 100
    }

    return Math.round((this.completedCount / this.selectedCount) * 100)
  }

  /**
   * Stops processing the items that have not started yet.
   */
  @action.bound
  cancel() {
    this.cancelled = true
  }

  /**
   * Runs the request for each item, a few at a time, until done or cancelled.
   */
  process = task.resolved(
    async (
      items: OCRScopeWork[],
      request: (item: OCRScopeWork) => Promise<unknown>,
    ): Promise<BulkProgressResult> => {
      if (!items.length) {
        return { succeeded: 0, failed: 0 }
      }

      runInAction(() => {
        this.selectedCount = items.length
        this.completedCount = 0
        this.errorCount = 0
        this.cancelled = false
        this.modal.show()
      })

      await lastValueFrom(
        from(items).pipe(
          mergeMap(async (item) => {
            if (this.cancelled) {
              return
            }

            await request(item).catch(
              action(() => {
                this.errorCount++
              }),
            )
            runInAction(() => {
              this.completedCount++
            })
          }, 5),
        ),
      )

      return {
        succeeded: this.completedSuccessfullyCount,
        failed: this.errorCount,
      }
    },
  )
}

/**
 * Outcome of a bulk action.
 */
export interface BulkProgressResult {
  succeeded: number
  failed: number
}
